import { useState, useRef, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useLocation } from "wouter";
import * as XLSX from "xlsx";
import { Download, Upload, AlertCircle, CheckCircle2, XCircle } from "lucide-react";

type MovementType = "ENTRADA" | "INTERNA" | "SAIDA" | "REPOSICAO";

interface ParsedMovement {
  tipo: MovementType;
  produto: string;
  origem?: string;
  destino?: string;
  responsavel: string;
  referencia?: string;
  observacoes?: string;
  numeroPedido?: string;
  removidoTotalmente?: boolean;
}

export default function Import() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [parsedData, setParsedData] = useState<ParsedMovement[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    total: number;
    success: number;
    errors: number;
    details: Array<{ success: boolean; error?: string; row: number }>;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bulkCreateMutation = trpc.movements.bulkCreate.useMutation();

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const downloadTemplate = () => {
    const headers = ["Tipo", "Produto", "Origem", "Destino", "Responsável", "Referência", "Observações", "Número Pedido", "Removido Totalmente"];
    const data = [
      ["ENTRADA", "Produto Exemplo", "", "A1-01", "João Silva", "REF-001", "Observação exemplo", "", "Sim"],
      ["INTERNA", "Produto Exemplo 2", "A1-01", "B2-05", "Maria Santos", "REF-002", "", "", "Sim"],
      ["INTERNA", "Produto Exemplo 2B", "A1-01", "B2-05", "Maria Santos", "REF-002B", "Item continua na origem", "", "Não"],
      ["SAIDA", "Produto Exemplo 3", "C3-10", "", "Pedro Costa", "REF-003", "", "", "Sim"],
      ["REPOSICAO", "Produto Exemplo 4", "D4-15", "E5-20", "Ana Lima", "REF-004", "Reposição urgente", "PED-12345", "Sim"],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Movimentações");
    XLSX.writeFile(workbook, "template_importacao.xlsx");
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

        const movements: ParsedMovement[] = [];

        for (const row of jsonData) {
          const tipo = row["Tipo"]?.toString().trim().toUpperCase();
          if (!["ENTRADA", "INTERNA", "SAIDA", "REPOSICAO"].includes(tipo)) {
            continue;
          }

          const removidoTotalmenteStr = row["Removido Totalmente"]?.toString().trim().toUpperCase();
          const removidoTotalmente = removidoTotalmenteStr === "SIM" || removidoTotalmenteStr === "TRUE" || removidoTotalmenteStr === "1";
          
          movements.push({
            tipo: tipo as MovementType,
            produto: row["Produto"]?.toString().trim() || "",
            origem: row["Origem"]?.toString().trim() || undefined,
            destino: row["Destino"]?.toString().trim() || undefined,
            responsavel: row["Responsável"]?.toString().trim() || "",
            referencia: row["Referência"]?.toString().trim() || undefined,
            observacoes: row["Observações"]?.toString().trim() || undefined,
            numeroPedido: row["Número Pedido"]?.toString().trim() || undefined,
            removidoTotalmente: (tipo === "INTERNA" || tipo === "REPOSICAO") ? removidoTotalmente : undefined,
          });
        }

        setParsedData(movements);
        setImportResult(null);
        toast.success(`${movements.length} movimentações carregadas para preview`);
      } catch (error) {
        toast.error(`Erro ao processar arquivo: ${error instanceof Error ? error.message : "Erro desconhecido"}`);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      toast.error("Nenhum dado para importar");
      return;
    }

    setImporting(true);
    try {
      const result = await bulkCreateMutation.mutateAsync({
        movements: parsedData,
      });

      setImportResult(result);

      if (result.errors === 0) {
        toast.success(`${result.success} movimentações importadas com sucesso!`);
        setParsedData([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        toast.warning(
          `${result.success} importadas, ${result.errors} com erro. Veja os detalhes abaixo.`
        );
      }
    } catch (error) {
      toast.error("Erro ao importar movimentações");
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => setLocation("/")}>
              ← Voltar
            </Button>
            <h1 className="text-2xl font-bold">Importação em Massa</h1>
          </div>
          <div className="text-sm text-muted-foreground">
            {user.name} ({user.email})
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-6">
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">1. Baixe o template</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Baixe o arquivo modelo Excel e preencha com suas movimentações.
          </p>
          <Button onClick={downloadTemplate} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Baixar Template Excel
          </Button>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">2. Faça upload do arquivo preenchido</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Selecione o arquivo Excel preenchido para visualizar um preview antes de importar.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            onChange={handleFileSelect}
            className="block w-full text-sm text-muted-foreground
              file:mr-4 file:py-2 file:px-4
              file:rounded file:border-0
              file:text-sm file:font-semibold
              file:bg-primary file:text-primary-foreground
              hover:file:bg-primary/90
              cursor-pointer"
          />
        </Card>

        {parsedData.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                3. Preview ({parsedData.length} movimentações)
              </h2>
              <Button onClick={handleImport} disabled={importing}>
                <Upload className="w-4 h-4 mr-2" />
                {importing ? "Importando..." : "Importar Tudo"}
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold">#</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold">Tipo</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold">Produto</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold">Origem</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold">Destino</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold">Responsável</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold">Nº Pedido</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {parsedData.map((movement, index) => (
                    <tr key={index} className="hover:bg-muted/30">
                      <td className="py-2 px-3 text-muted-foreground">{index + 1}</td>
                      <td className="py-2 px-3">{movement.tipo}</td>
                      <td className="py-2 px-3 font-medium">{movement.produto}</td>
                      <td className="py-2 px-3">{movement.origem || "-"}</td>
                      <td className="py-2 px-3">{movement.destino || "-"}</td>
                      <td className="py-2 px-3">{movement.responsavel}</td>
                      <td className="py-2 px-3">{movement.numeroPedido || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {importResult && (
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Resultado da Importação</h2>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-blue-500" />
                <div>
                  <div className="text-2xl font-bold">{importResult.total}</div>
                  <div className="text-xs text-muted-foreground">Total</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <div>
                  <div className="text-2xl font-bold text-green-600">{importResult.success}</div>
                  <div className="text-xs text-muted-foreground">Sucesso</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                <div>
                  <div className="text-2xl font-bold text-red-600">{importResult.errors}</div>
                  <div className="text-xs text-muted-foreground">Erros</div>
                </div>
              </div>
            </div>

            {importResult.details.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2 text-red-600">Linhas com Erro:</h3>
                <div className="space-y-2">
                  {importResult.details.map((detail, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 text-sm bg-red-50 p-2 rounded"
                    >
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5" />
                      <div>
                        <span className="font-semibold">Linha {detail.row}:</span>{" "}
                        {detail.error}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}
      </main>
    </div>
  );
}
